import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { message } from "antd";
import { useTheme } from "../../ThemeContext";
import { initiateSSOLogin } from "../../utils/apiClient";

const SamlLoginModal = ({ open, onClose }) => {
  const { currentTheme, themeMode } = useTheme();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^.{1,64}@[^\s@]+(\.[^\s@]+){1,255}$/;
    if (!email.trim()) {
      return "Email is required";
    }
    if (!emailRegex.test(email)) {
      return "Please enter a valid email address";
    }
    return null;
  };

  const handleEmailChange = (event) => {
    setEmail(event.target.value);
    // Clear error when user starts typing
    if (error) {
      setError("");
    }
  };

  const handleSubmit = async () => {
    const emailError = validateEmail(email);
    if (emailError) {
      setError(emailError);
      return;
    }

    setIsSubmitting(true);
    try {
      message.loading("Redirecting to SAML...", 0);
      const response = await initiateSSOLogin(email.trim());
      message.destroy();
      
      if (response && response.response.sso_url) {
        window.location.href = response.response.sso_url;
      } else {
        message.error("Failed to get SAML login URL. Please try again.");
        setError("Failed to get SAML login URL. Please try again.");
      }
    } catch (err) {
      message.destroy();
      // Try to extract a backend error message
      const backendMsg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to initiate SAML login.";
      message.error(backendMsg);
      setError(backendMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setEmail("");
      setError("");
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: themeMode === "dark" ? "#04002b" : "#ffffff",
          color:
            currentTheme.homePage?.textColor ||
            (themeMode === "dark" ? "#ffffff" : "#000000"),
          borderRadius: "16px",
          border:
            themeMode === "dark" ? "1px solid #302d4c" : "1px solid #e9e8ea",
          minHeight: "30vh",
          fontFamily:
            '"Satoshi", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
          boxShadow:
            themeMode === "dark"
              ? "0 20px 40px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255,255,255,0.1)"
              : "0 20px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0,0,0,0.05)",
        },
      }}
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography
            variant="h4"
            sx={{
              color: currentTheme.text.primary,
              marginTop: "2rem",
              fontWeight: 600,
              fontFamily: '"Satoshi", sans-serif',
              fontSize: "1.75rem",
              "@media (max-width: 480px)": {
                fontSize: "1.4rem",
              },
            }}
          >
            Sign in with SAML
          </Typography>
          <IconButton
            onClick={handleClose}
            disabled={isSubmitting}
            sx={{ color: currentTheme.text.primary }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pb: 3 }}>
        <Box component="form" sx={{ mt: 2 }}>
          <Typography
            variant="body1"
            sx={{
              color: currentTheme.text.primary,
              mb: 3,
              mt: -2,
              fontFamily: '"Satoshi", sans-serif',
              fontSize: "1rem",
              lineHeight: 1.5,
            }}
          >
            Enter your email address to sign in with your organization's SAML provider.
          </Typography>

          {/* Email Field */}
          <Typography
            variant="body1"
            sx={{
              color: currentTheme.text.primary,
              mb: 1,
              mt: 4,
              fontFamily: '"Satoshi", sans-serif',
              fontSize: "1rem",
              fontWeight: 500,
            }}
          >
            Email Address*
          </Typography>
          <TextField
            fullWidth
            type="email"
            value={email}
            onChange={handleEmailChange}
            error={!!error}
            helperText={error}
            placeholder="Enter your email address"
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor:
                  themeMode === "dark" ? "rgba(255,255,255,0.05)" : "#f7f7f7",
                borderRadius: 2,
                fontFamily: '"Satoshi", sans-serif',
                fontSize: "1rem",
                "& fieldset": {
                  borderColor: themeMode === "dark" ? "#302d4c" : "#e9e8ea",
                },
                "&:hover fieldset": {
                  borderColor: "#FE4E3A",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#FE4E3A",
                },
              },
              "& .MuiInputBase-input": {
                color: themeMode === "dark" ? "#999999" : "#666666",
                fontFamily: '"Satoshi", sans-serif',
                fontSize: "1rem",
                padding: "1rem",
                "&::placeholder": {
                  color: themeMode === "dark" ? "#999999" : "#666666",
                  opacity: 1,
                },
              },
              "& .MuiFormHelperText-root": {
                color: "#FE4E3A",
                fontFamily: '"Satoshi", sans-serif',
                fontSize: "0.875rem",
                marginTop: "0.25rem",
              },
            }}
          />

          {/* Action Buttons */}
          <Box display="flex" justifyContent="flex-end" gap={2} sx={{ mt: 4 }}>
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={isSubmitting}
              sx={{
                borderColor: themeMode === "dark" ? "#302d4c" : "#666",
                color: themeMode === "dark" ? "#ffffff" : "#666",
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 600,
                fontFamily: '"Satoshi", sans-serif',
                "&:hover": {
                  borderColor: "#888",
                  backgroundColor:
                    themeMode === "dark" ? "rgba(255,255,255,0.05)" : "#f5f5f5",
                },
                "@media (max-width: 480px)": {
                  px: 3,
                  py: 1.25,
                  fontSize: "0.875rem",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting}
              sx={{
                background: "#FE4E3A",
                color: "#fff",
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 600,
                fontFamily: '"Satoshi", sans-serif',
                "&:hover": {
                  background: "#FF4444",
                  transform: "translateY(-1px)",
                  boxShadow: "0 4px 12px rgba(255, 92, 92, 0.3)",
                },
                "&:disabled": {
                  background: themeMode === "dark" ? "#302d4c" : "#999",
                  color: "#cccccc",
                  transform: "none",
                  boxShadow: "none",
                },
                "@media (max-width: 480px)": {
                  px: 3,
                  py: 1.25,
                  fontSize: "0.875rem",
                },
              }}
            >
              {isSubmitting ? "Redirecting..." : "Sign in with SAML"}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SamlLoginModal; 